import React, { useMemo } from 'react';
import { Box, Button, Container, Typography, useTheme, alpha } from '@mui/material';
import { ArrowForward as ArrowForwardIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCategories } from '../../../context/CategoryContext';
import { useCategoryName } from '../../../utils/categoryTranslation';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';
const BASE_URL = API_URL.replace('/api/v1', '');

const getCategoryImageUrl = (category) => {
  const value = category.imageUrl || category.image_url || category.icon || null;
  if (!value) return null;
  if (value.startsWith('http')) return value;
  return `${BASE_URL}/${String(value).replace(/^\//, '')}`;
};

const HomePopularCategoriesSection = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { categories, categoriesList } = useCategories();
  const getCategoryName = useCategoryName();

  const parentCategories = useMemo(() => {
    const source = Array.isArray(categories) && categories.length > 0 ? categories : categoriesList;
    if (!Array.isArray(source)) return [];
    return source
      .filter((category) => !category.parentId && category.isActive !== false)
      .slice(0, 8);
  }, [categories, categoriesList]);

  if (parentCategories.length === 0) return null;

  return (
    <Box component="section" sx={{ py: { xs: 4, md: 5 }, bgcolor: 'background.default' }}>
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            mb: { xs: 2.5, md: 3 },
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="h4" component="h2" sx={{ fontWeight: 800 }}>
            {t('homepage.categories.title')}
          </Typography>
          <Button
            endIcon={<ArrowForwardIcon />}
            onClick={() => navigate('/categories')}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            {t('homepage.categories.viewAll')}
          </Button>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)', lg: 'repeat(8, 1fr)' },
            gap: { xs: 1.5, md: 2 },
          }}
        >
          {parentCategories.map((category) => {
            const imageUrl = getCategoryImageUrl(category);
            const label = getCategoryName(category) || category.name;
            return (
              <Box
                key={category.id}
                onClick={() => navigate(`/search?category=${category.id}`)}
                sx={{
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.8,
                  '&:hover .cat-media': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 12px 24px ${alpha(theme.palette.primary.main, 0.18)}`,
                  },
                }}
              >
                <Box
                  className="cat-media"
                  sx={{
                    width: '100%',
                    aspectRatio: '1 / 1',
                    borderRadius: 3,
                    overflow: 'hidden',
                    backgroundColor: '#fff',
                    border: '1px solid #ececec',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {imageUrl ? (
                    <Box
                      component="img"
                      src={imageUrl}
                      alt={label}
                      loading="lazy"
                      decoding="async"
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <Typography sx={{ fontSize: { xs: '2rem', md: '2.25rem' } }}>📦</Typography>
                  )}
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    textAlign: 'center',
                    fontWeight: 700,
                    lineHeight: 1.25,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Container>
    </Box>
  );
};

export default HomePopularCategoriesSection;